import { BankOutlined } from '@ant-design/icons';
import { Button, Layout, Table } from 'antd';
import fileDownload from 'js-file-download';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { renderBillsColumnStatus } from 'app/components/commons/Bills/Bills';
import { BillingInformationModal } from 'app/components/pages/Extranet/commons/BillingInformation/BillingInformationModal';
import { PaginationButton } from 'app/components/pages/Extranet/commons/PaginationButton/PaginationButton';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useBills } from 'app/hooks/data/core/extranet/bills/useBills';
import { downloadBill, resetBillObjectPdf } from 'app/redux/actions/hotelAdmin';
import { useAppDispatch, useAppSelector } from 'app/redux/hooks';
import {
  selectHotelAdminBillsIsDownloading,
  selectHotelAdminBillsPdfObject,
} from 'app/redux/selectors/hotelAdmin';
import type { PayoutStatus } from 'app/typings/bills';
import type { ExtranetBill } from 'app/typings/core/extranet/bills/bill';
import { dateAndHour } from 'app/utils/dates';
import { dayjs } from 'app/utils/dayjs';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { useQueryParamHistory } from 'app/utils/queryParamHistory';
import { scrollToTop } from 'app/utils/scroll';
import type { SortOrder } from 'app/utils/sort';
import {
  convertFromAntdSortOrder,
  convertToAntdSortOrder,
  formatSortingForAPI,
  getSortOrder,
  isValidSortOrder,
} from 'app/utils/sort';
import { formatCurrency, getCurrencyLocale } from 'app/utils/strings';
import { useTrackViewPage } from 'app/utils/track/amplitude';

import { ExtranetHeader } from '../commons/ExtranetHeader/ExtranetHeader';

import './ExtranetBills.scss';
import { BillsActionMenu } from './_components/BillsActionMenu/BillsActionMenu';

type BillsQueryParam = {
  pageNumber: number;
  dateSorting: SortOrder;
};

const { Content } = Layout;
const { Column } = Table;

const PAGE_SIZE = 50;

const makePayload = (queryParam: Partial<BillsQueryParam>) => {
  const validSortOrder = isValidSortOrder(queryParam.dateSorting);

  return {
    sortingColumn: validSortOrder ? 'createdAt' : undefined,
    sortingOrder: validSortOrder
      ? formatSortingForAPI(queryParam.dateSorting)
      : undefined,
    offset: ((queryParam.pageNumber || 1) - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  };
};

const queryParamInit = {
  pageNumber: 1,
  dateSorting: 'descend',
} as BillsQueryParam;
const queryParamKeys = {
  pageNumber: 'p',
  dateSorting: 'ds',
};
const queryParamFormatter = {};
const queryParamExtractor = {
  pageNumber: parseInt,
  dateSorting: getSortOrder,
};

const PAGE = 'Bills';
const BILLING_INFORMATION_QUERY_PARAM = 'billingInformation';
const PERIOD_DATE_FORMAT = 'DD/MM/YYYY';

export const ExtranetBills = () => {
  const { setPage } = useContext(TaskCenterContext);
  const { hotelId } = useContext(HotelContext);
  const history = useHistory();
  const location = useLocation();

  const [isBillingInformationOpen, setIsBillingInformationOpen] =
    useState(false);

  const dispatch = useAppDispatch();
  const billPdfObject = useAppSelector(selectHotelAdminBillsPdfObject);
  const isDownloading = useAppSelector(selectHotelAdminBillsIsDownloading);

  useEffect(() => {
    setPage(PAGE);
  });

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    if (query.get(BILLING_INFORMATION_QUERY_PARAM) !== 'true') {
      return;
    }

    setIsBillingInformationOpen(true);
    query.delete(BILLING_INFORMATION_QUERY_PARAM);
    history.replace({ search: query.toString() });
  }, [history, location.search]);

  const { queryParam, updateQueryParam } =
    useQueryParamHistory<BillsQueryParam>(
      queryParamInit,
      queryParamKeys,
      queryParamFormatter,
      queryParamExtractor
    );

  const debouncedQueryParam = useDebounce(queryParam, 500);

  const { data: bills = [], isFetching: isLoading } = useBills(
    hotelId as number,
    makePayload(debouncedQueryParam)
  );

  const handleDownload = useCallback(
    (billId: number) => {
      if (!isDownloading) {
        dispatch(downloadBill({ billId, hotelId: hotelId as number }));
      }
    },
    [dispatch, isDownloading, hotelId]
  );

  useEffect(() => {
    if (billPdfObject) {
      fileDownload(
        billPdfObject.pdf,
        billPdfObject.filename,
        'application/pdf'
      );
      dispatch(resetBillObjectPdf());
    }
  }, [billPdfObject, dispatch]);

  useTrackViewPage({ page: 'bills' });

  const handleOffset = (x: number) => {
    updateQueryParam({ pageNumber: (queryParam?.pageNumber || 1) + x });

    scrollToTop();
  };

  const dateSortingHandler = (sort: SortOrder) => {
    updateQueryParam({
      dateSorting: sort,
      pageNumber: 1,
    });
  };

  const handleRowActions = ({ id }: { id: number }) => ({
    onClick: () => handleDownload(id),
  });

  const paginationLeftButtonDisabled = queryParam.pageNumber === 1;
  const paginationRightButtonDisabled = bills.length < PAGE_SIZE;

  return (
    <Layout className="bills-list">
      <ExtranetHeader page={PAGE} />
      <Content className="body">
        <Table
          dataSource={bills}
          pagination={false}
          title={() => (
            <div className="billsHeader">
              <Button
                icon={<BankOutlined />}
                onClick={() => setIsBillingInformationOpen(true)}
              >
                Billing information
              </Button>
              <PaginationButton
                leftButtonDisabled={paginationLeftButtonDisabled}
                rightButtonDisabled={paginationRightButtonDisabled}
                onPrev={() => handleOffset(-1)}
                onNext={() => handleOffset(1)}
                pageNumber={queryParam.pageNumber as number}
              />
            </div>
          )}
          loading={isLoading}
          rowKey="id"
          onRow={handleRowActions}
          onChange={(__, ___, sorter) => {
            // @ts-ignore -- legacy directive, needs investigation
            const { order } = sorter;

            dateSortingHandler(convertFromAntdSortOrder(order));
          }}
        >
          <Column title="Invoice reference #" dataIndex="reference" />
          <Column
            title="Total net amount"
            dataIndex="totalAmount"
            render={(price: number, bill: ExtranetBill) =>
              formatCurrency(
                price,
                bill.currency,
                'Missing',
                getCurrencyLocale(bill.currency)
              )
            }
          />

          <Column
            title="Creation date"
            dataIndex="createdAt"
            sorter
            sortOrder={convertToAntdSortOrder(queryParam.dateSorting)}
            render={(date) => date && dateAndHour(date)}
          />
          <Column
            title="Period"
            render={({ fromDate, toDate }) => {
              if (fromDate && toDate) {
                const from = dayjs(fromDate).format(PERIOD_DATE_FORMAT);
                const to = dayjs(toDate).format(PERIOD_DATE_FORMAT);

                return `${from} - ${to}`;
              }

              return null;
            }}
          />
          <Column
            title="Payout status"
            dataIndex="status"
            render={(status: PayoutStatus, bill: ExtranetBill) =>
              renderBillsColumnStatus(status, bill, true)
            }
          />
          <Column
            render={({ id }) => (
              <BillsActionMenu onActionClick={() => handleDownload(id)} />
            )}
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
        </Table>
        <PaginationButton
          className="footer"
          leftButtonDisabled={paginationLeftButtonDisabled}
          rightButtonDisabled={paginationRightButtonDisabled}
          onPrev={() => handleOffset(-1)}
          onNext={() => handleOffset(1)}
          pageNumber={queryParam.pageNumber as number}
        />
      </Content>
      <BillingInformationModal
        isOpen={isBillingInformationOpen}
        source="bills"
        onClose={() => setIsBillingInformationOpen(false)}
      />
    </Layout>
  );
};
